import type { Auth } from 'firebase/auth';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import type { FirebaseStorage } from 'firebase/storage';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import type { Messaging } from 'firebase/messaging';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';
import type { FirebaseApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import { getFirebaseAnalytics, getFirebaseApp } from '@firebase-config';

export type FirebaseServices = {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
};

let cachedServices: FirebaseServices | undefined;

const shouldUseEmulators = (): boolean =>
  import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

export const initFirebaseClient = async (): Promise<FirebaseServices> => {
  if (cachedServices) {
    return cachedServices;
  }

  const app = getFirebaseApp();
  const auth = getAuth(app);
  const firestore = getFirestore(app);
  const functions = getFunctions(app, 'us-central1');
  const storage = getStorage(app);

  if (shouldUseEmulators()) {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(firestore, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
  }

  await getFirebaseAnalytics().catch(() => null);

  cachedServices = {
    app,
    auth,
    firestore,
    functions,
    storage,
  };

  return cachedServices;
};

let messagingInstance: Promise<Messaging | null> | null = null;

export const getMessagingClient = async (): Promise<Messaging | null> => {
  if (messagingInstance === null) {
    messagingInstance = isMessagingSupported().then(supported =>
      supported ? getMessaging(getFirebaseApp()) : null,
    );
  }

  return messagingInstance;
};

let analyticsInstance: Promise<Analytics | null> | null = null;

export const getAnalyticsClient = async (): Promise<Analytics | null> => {
  if (analyticsInstance === null) {
    analyticsInstance = getFirebaseAnalytics().catch(() => null);
  }

  return analyticsInstance;
};

// Direct exports for convenience
export const app = getFirebaseApp();
export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);
